import type { ButtonHTMLAttributes, ReactElement, ReactNode } from 'react'

import styles from './button.module.scss'

export type ButtonVariant = 'primary' | 'secondary' | 'danger'

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** The one centred label. `children` works too, and is what a spec reads. */
  label?: ReactNode
  variant?: ButtonVariant
  /** `lg` is the primary action of a plant screen — 5.6rem tall. */
  size?: 'md' | 'lg'
  fullWidth?: boolean
}

/**
 * The kit's only labelled call to action. It renders `<button><span>label</span>`:
 * one centred label in a pill, never a two-column row or a bare glyph — a control
 * that needs `display`, `text-align` and `padding` overridden to fit is a native
 * `<button>` with a comment saying why, not a bent Button.
 *
 * `type` defaults to `button`. Inside a form the default would be `submit`, and
 * an "Añadir tipo" that reloads the page by submitting the form around it is the
 * bug that default prevents.
 */
export const Button = ({
  label,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  type = 'button',
  className,
  children,
  ...rest
}: ButtonProps): ReactElement => {
  const classes = [
    styles.button,
    styles[`button--${variant}`],
    styles[`button--${size}`],
    fullWidth ? styles['button--full'] : '',
    className ?? '',
  ].filter(Boolean).join(' ')

  return (
    <button className={classes} type={type} {...rest}>
      <span>{label ?? children}</span>
    </button>
  )
}
